/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { connect, ConnectedProps, useSelector } from 'react-redux';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button, Form, FormControl } from 'react-bootstrap';
import {
  addChecklistItem,
  buttonFormError,
  makeEdit,
  moveChecklistItemDown,
  moveChecklistItemUp,
  removeChecklistItem,
  setName,
  setTitle,
  toggleChecklistItem,
  updateChecklistItem,
} from '../../../redux/button/button';
import LinksModal from '../links/linksModal';
import { ReactComponent as AddIcon } from '../../../assets/icons/addSection.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/sectiondeleteIcon.svg';
import { ReactComponent as MoveDown } from '../../../assets/icons/moveDownIcon.svg';
import { ReactComponent as MoveUp } from '../../../assets/icons/moveUpIcon.svg';
import Tooltip from '../../utils/tooltip';
import { ParentDataInterface } from '../../../redux/parentDataSlice';

export interface ButtonFormInterface {
  title: string;
  name: string;
  link: string;
  isChecked: boolean;
  errors: any;
  btnType: string;
  checklistItems: ChecklistItem[];
  fontFamily: string;
  fontStyle: string;
  fontSize: string;
  alignment: string;
  fontColor: string;
  customPaddingtop: string;
  customPaddingleft: string;
  customPaddingbottom: string;
  customPaddingright: string;
}

interface RootState {
  button: ButtonFormInterface;
}
export interface ChecklistItem {
  id: string;
  label: string;
  checked: boolean;
}

const mapState = ({ button }: RootState) => ({
  title: button.title,
  name: button.name,
  link: button.link,
  isChecked: button.isChecked,
  errors: button.errors,
  btnType: button.btnType,
  checklistItems: button.checklistItems,
  fontFamily: button.fontFamily,
  fontStyle: button.fontStyle,
  fontSize: button.fontSize,
  alignment: button.alignment,
  fontColor: button.fontColor,
  customPaddingtop: button.customPaddingtop,
  customPaddingleft: button.customPaddingleft,
  customPaddingbottom: button.customPaddingbottom,
  customPaddingright: button.customPaddingright,
});

const mapDispatch = (dispatch: Function) => ({
  SetTitle: (data: { title: string; errors: any }) => dispatch(setTitle(data)),
  SetName: (data: { name: string; errors: any }) => dispatch(setName(data)),
  MakeEdit: (data: any) => dispatch(makeEdit(data)),
  SetError: (data: { errors: any }) => dispatch(buttonFormError(data)),
  addChecklistItem: (data: { label: string }) => dispatch(addChecklistItem(data)),
  removeChecklistItem: (id: string) => dispatch(removeChecklistItem(id)),
  updateChecklistItem: (data: { id: string; value: string }) => dispatch(updateChecklistItem(data)),
  toggleChecklistItem: (data: { id: string }) => dispatch(toggleChecklistItem(data)),
  moveChecklistItemUp: (index: number) => dispatch(moveChecklistItemUp(index)),
  moveChecklistItemDown: (index: number) => dispatch(moveChecklistItemDown(index)),
});

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux &
  ButtonFormInterface & {
    editContent: any;
    pathName: string;
    id?: string;
    elementType: string;
  };

const ButtonFormComponent = (props: Props) => {
  const {
    id,
    name,
    link,
    title,
    errors,
    SetName,
    SetTitle,
    SetError,
    MakeEdit,
    editContent,
    pathName,
    elementType,
  } = props;
  const [showModal, setShowModal] = useState(false);
  const hasInitialized = React.useRef(false);
  const interactiveState = useSelector(
    (state: ParentDataInterface) => state.parentData.interactiveState,
  );
  const [isReadOnly] = useState(interactiveState === 'collaborative' && id ? true : false);

  useEffect(() => {
    if (id) {
      const isDuplicate = pathName.includes('duplicate');
      if (isDuplicate) {
        SetError({
          errors: { title: 'DUPLICATED INTERACTIVE - Please enter a new title' },
        });
      }
      const { parameters, customisations, title } = editContent;
      const checklistWithIds =
        editContent.parameters?.checklistItems?.map((item: any) => ({
          ...item,
          id: item.id ?? crypto.randomUUID(),
        })) ?? [];

      MakeEdit({
        title: isDuplicate ? '' : title,
        name: parameters.name,
        link: parameters.link,
        isChecked: parameters.isChecked,
        color: customisations.bgColor !== '' ? customisations.bgColor : '#007bff',
        btnType: customisations.btnType !== '' ? customisations.btnType : 'btn btn-square btn-dark',
        checklistItems: checklistWithIds,
        fontFamily: customisations.fontFamily !== '' ? customisations.fontFamily : 'Helvetica',
        fontStyle: customisations.fontStyle,
        fontSize: customisations.fontSize !== '' ? customisations.fontSize : '16',
        alignment: customisations.alignment !== '' ? customisations.alignment : 'left',
        fontColor: customisations.fontColor !== '' ? customisations.fontColor : '#ffffff',
        customPaddingtop: customisations.customPaddingtop ? customisations.customPaddingtop : '10',
        customPaddingleft: customisations.customPaddingleft
          ? customisations.customPaddingleft
          : '10',
        customPaddingbottom: customisations.customPaddingbottom
          ? customisations.customPaddingbottom
          : '10',
        customPaddingright: customisations.customPaddingright
          ? customisations.customPaddingright
          : '10',
        ChecklistBgColor: customisations.ChecklistBgColor,
        borderStyle: customisations.borderStyle,
        borderColor: customisations.borderColor,
        borderTop: customisations.borderTop,
        borderBottom: customisations.borderBottom,
        borderLeft: customisations.borderLeft,
        borderRight: customisations.borderRight,
        borderRadius: customisations.borderRadius,
        checklistBodyFontSize: customisations.checklistBodyFontSize,
        checklistBodyFontStyle: customisations.checklistBodyFontStyle,
        ChecklistBodyFontColor: customisations.ChecklistBodyFontColor,
        checklistBodyAlignment: customisations.checklistBodyAlignment,
        checklistBodyFontWeight: customisations.checklistBodyFontWeight,
        headerFontColor: customisations.headerFontColor,
        strike: customisations.strike,
        bodyFontFamily: customisations.bodyFontFamily,
        checkBoxColor: customisations.checkBoxColor,
        fontWeightForHeader: customisations.fontWeightForHeader,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editContent, MakeEdit, SetError, pathName]);

  useEffect(() => {
    if (!hasInitialized.current && props.checklistItems.length === 0 && !props.id) {
      props.addChecklistItem({ label: '' });
      props.addChecklistItem({ label: '' });
      hasInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.checklistItems.length]);

  const handleInputChange = (event: ChangeEvent, type: string) => {
    let fail = { ...errors };
    delete fail[`${type}`];
    const { value } = event.target as HTMLInputElement;
    switch (type) {
      case 'title':
        SetTitle({
          title: value.trimStart(),
          errors: fail,
        });
        break;
      case 'name':
        SetName({
          name: value.trimStart(),
          errors: fail,
        });
        break;
    }
  };

  return (
    <Form className='' onSubmit={(e) => e.preventDefault()}>
      <Form.Group className='py-2'>
        <Form.Label
          className='title'
          style={{ fontFamily: 'lexend', fontSize: '14px', color: '#707070', fontWeight: 300 }}
        >
          Interactive Title *
        </Form.Label>
        <FormControl
          required
          value={title.replace(/ +/g, ' ')}
          className='lint-custom-input'
          data-testid='interactive-title'
          id='interactive-title'
          autoComplete='off'
          type='text'
          onChange={(event) => handleInputChange(event, 'title')}
          maxLength={100}
          readOnly={isReadOnly}
          style={{
            ...(isReadOnly ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
          }}
        />
        {errors.title && <Form.Text className='text-danger'>{errors.title}</Form.Text>}
      </Form.Group>
      <Form.Group className='py-2'>
        <Form.Label
          className='title'
          style={{ fontFamily: 'lexend', fontSize: '14px', color: '#707070', fontWeight: 300 }}
        >
          {elementType === 'checklist' ? 'Header ' : 'Button  Name '}*
        </Form.Label>
        <FormControl
          required
          value={name.replace(/ +/g, ' ')}
          className='lint-custom-input'
          data-testid='button-name'
          id='button-name'
          onChange={(event) => handleInputChange(event, 'name')}
          autoComplete='off'
        />
        {errors.name && <Form.Text className='text-danger'>{errors.name}</Form.Text>}
      </Form.Group>
      {elementType === 'Button' && (
        <Form.Group className='py-2'>
          <Form.Label
            className='title'
            style={{ fontFamily: 'lexend', fontSize: '14px', color: '#707070', fontWeight: 300 }}
          >
            Button Link *
          </Form.Label>
          <div className='d-flex justify-content-start align-items-center gap-3'>
            <Button className='addLinkButton' onClick={() => setShowModal(true)}>
              {link.length > 0 ? 'Update ' : 'Add '}Link
            </Button>
            <FormControl
              required
              value={link.replace(/ +/g, ' ')}
              className='lint-custom-input'
              data-testid='button-link'
              id='button-link'
              disabled
              placeholder='Upon selection, link url will display here.'
              maxLength={256}
              style={{
                width: '50%',
              }}
            />
          </div>
          <LinksModal showModal={showModal} onHide={() => setShowModal(false)} type='button' />
          {errors.link && <Form.Text className='text-danger'>{errors.link}</Form.Text>}
        </Form.Group>
      )}
      {elementType === 'checklist' && (
        <Form.Group className='checklistItemsGroup mt-2'>
          <Form.Label className='checklistBodyTitle'>Body</Form.Label>
          <div className='checklistBodyData'>
            {props.checklistItems.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === props.checklistItems.length - 1;

              return (
                <div key={item.id} className='p-2 gap-2'>
                  <div className='d-flex'>
                    <div className='checklistItemLabel'>Checklist {index + 1} *</div>

                    {!isFirst && (
                      <Button
                        className='buttonForChecklist'
                        size='sm'
                        onClick={() => props.moveChecklistItemUp(index)}
                      >
                        <Tooltip info='Move up'>
                          <MoveUp />
                        </Tooltip>
                      </Button>
                    )}

                    {!isLast && (
                      <Button
                        size='sm'
                        className='buttonForChecklist'
                        onClick={() => props.moveChecklistItemDown(index)}
                      >
                        <Tooltip info='Move down'>
                          <MoveDown />
                        </Tooltip>
                      </Button>
                    )}

                    {props.checklistItems.length > 1 && (
                      <Button
                        size='sm'
                        className='buttonForChecklist'
                        onClick={() => props.removeChecklistItem(item.id)}
                      >
                        <Tooltip info='Delete'>
                          <DeleteIcon />
                        </Tooltip>
                      </Button>
                    )}
                  </div>
                  <FormControl
                    className='lint-custom-input'
                    value={item.label}
                    onChange={(e) => {
                      props.updateChecklistItem({
                        id: item.id,
                        value: e.target.value.trimStart(),
                      });
                      if (errors?.checklistItemErrors?.[index]) {
                        const updatedErrors: any = {
                          ...errors,
                          checklistItemErrors: { ...errors.checklistItemErrors },
                        };

                        delete updatedErrors.checklistItemErrors[index];
                        if (Object.keys(updatedErrors.checklistItemErrors).length === 0) {
                          delete updatedErrors.checklistItemErrors;
                        }
                        if (Object.keys(updatedErrors).length === 0) {
                          props.SetError({ errors: {} });
                        } else {
                          props.SetError({ errors: updatedErrors });
                        }
                      }
                    }}
                  />
                  {errors?.checklistItemErrors?.[index] && (
                    <Form.Text className='text-danger'>
                      {errors.checklistItemErrors[index]}
                    </Form.Text>
                  )}
                </div>
              );
            })}
          </div>
          <div key='addPanel' className='addpanel-div position-relative my-5'>
            <Button
              size='sm'
              onClick={() => {
                props.addChecklistItem({
                  label: '',
                });
              }}
              className=' position-absolute AddChecklistItemButton'
            >
              Add Check List <AddIcon /> 
            </Button>
          </div>
        </Form.Group>
      )}
    </Form>
  );
};

export default connector(ButtonFormComponent);
